import { Fragment } from "react";
import { Td, Th } from "../typography/styled";
import { TableWrapper } from "./styled";
import { BaseInput } from "../form/input/styled";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import defaultHeadshot from "../../assets/images/profilebasefavicon.svg";

export const Table = ({
  columnTitles,
  rowItems,
  location,
  activeRowId,
  handleRowItemClick,
  handleRowItemActionClick,
  handleTableHeaderClick,
  handleChange,
  dropdownRef,
}) => {
  const navigate = useNavigate();

  const getMonthName = (
    monthIndex,
    year = new Date().getFullYear(),
    locale = "en-US",
  ) => {
    const date = new Date(year, monthIndex - 1);
    return new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
  };

  const monetaryColumnsByLocation = {
    "Summary Table": [1, 2, 3],
    "Variance Table": [1, 2, 3],
    "General Table": Array.from(
      { length: Math.max(columnTitles.length - 6, 0) },
      (_, index) => index + 6,
    ),
    "Paye Table": [2, 3],
    "Pension Table": [3, 4, 5, 6],
    "Payroll Report Table": [1, 2, 3, 4],
  };
  const monetaryColumns = monetaryColumnsByLocation[location] || [];

  const getMonetaryValue = (rowItem, columnIndex) => {
    const valuesByLocation = {
      "Summary Table": [
        rowItem?.totalEarnings,
        rowItem?.totalDeductions,
        rowItem?.netPay,
      ],
      "Variance Table": [
        rowItem?.firstMonthValue,
        rowItem?.secondMonthValue,
        rowItem?.variance,
      ],
      "Paye Table": [rowItem?.grossPay, rowItem?.payeValue],
      "Pension Table": [
        rowItem?.grossPay,
        rowItem?.employerPensionValue,
        rowItem?.employeePensionValue,
        rowItem?.pensionValue,
      ],
      "Payroll Report Table": [
        rowItem?.totalEarnings,
        rowItem?.totalDeductions,
        rowItem?.grossPay,
        rowItem?.netPay,
      ],
    };

    if (location === "General Table") {
      if (columnIndex < 6) return 0;
      const variableIndex = columnIndex - 6;
      const variableValues = [
        ...(rowItem?.earnings || []),
        ...(rowItem?.deductions || []),
      ];
      if (variableIndex < variableValues.length) {
        return Number(variableValues[variableIndex]?.value) || 0;
      }
      const fixedValues = [
        rowItem?.totalEarnings,
        rowItem?.totalDeductions,
        rowItem?.grossPay,
        rowItem?.netPay,
      ];
      return Number(fixedValues[variableIndex - variableValues.length]) || 0;
    }

    const valueIndex = monetaryColumns.indexOf(columnIndex);
    return Number(valuesByLocation[location]?.[valueIndex]) || 0;
  };

  const totals = monetaryColumns.reduce((result, columnIndex) => {
    result[columnIndex] = (rowItems || []).reduce(
      (total, rowItem) => total + getMonetaryValue(rowItem, columnIndex),
      0,
    );
    return result;
  }, {});

  return (
    <TableWrapper>
      <thead
        onClick={() =>
          location === "Payroll Report Table" && handleTableHeaderClick()
        }
      >
        <tr>
          {columnTitles.map((columnTitle, index) => {
            return <Th key={index}>{columnTitle}</Th>;
          })}
        </tr>
      </thead>
      <tbody>
        {rowItems?.map((rowItem, index) => {
          const capitalizeWords = (str) => {
            return str?.replace(/\b\w/g, (char) => char?.toUpperCase());
          };
          return (
            <tr
              key={index}
              onClick={(e) =>
                location === "Plans & Pricing" &&
                handleRowItemClick?.(e, rowItem?.id)
              }
              style={
                location === "Plans & Pricing"
                  ? { cursor: "pointer" }
                  : undefined
              }
            >
              {location === "Employee Table" && (
                <Fragment>
                  <Td>{rowItem?.fullName || ""}</Td>
                  <Td>
                    {capitalizeWords(rowItem?.jobInformation?.department) ??
                      "Not Assigned"}
                  </Td>
                  <Td>
                    {rowItem?.payrollSetupInformation?.annualGrossPay?.toLocaleString() ||
                      ""}
                  </Td>
                  <Td>{rowItem?.jobInformation?.dateHired || ""}</Td>
                  <Td>
                    {capitalizeWords(rowItem?.jobInformation?.jobPosition) ||
                      ""}
                  </Td>
                  <Td
                    onClick={(e) => handleRowItemClick(e, rowItem?.employeeId)}
                  >
                    <FontAwesomeIcon
                      icon={faEllipsisV}
                      style={{
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                    />
                    {activeRowId === rowItem?.employeeId && (
                      <ul ref={dropdownRef} className="drop-down">
                        <li
                          onClick={(e) =>
                            handleRowItemActionClick(
                              e,
                              rowItem?.employeeId,
                              "edit",
                            )
                          }
                        >
                          Edit Employee
                        </li>
                        <li
                          onClick={(e) =>
                            handleRowItemActionClick(
                              e,
                              rowItem?.employeeId,
                              "delete",
                            )
                          }
                        >
                          Delete Employee
                        </li>
                      </ul>
                    )}
                  </Td>
                </Fragment>
              )}
              {location === "Dashboard Employee Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.fullName) || ""}</Td>
                  <Td>
                    {capitalizeWords(rowItem?.jobInformation?.department) ??
                      "Not Assigned"}
                  </Td>
                  <Td>
                    {rowItem?.payrollSetupInformation?.annualGrossPay?.toLocaleString() ||
                      ""}
                  </Td>
                  <Td>{rowItem?.jobInformation?.dateHired || ""}</Td>
                  <Td>
                    {capitalizeWords(rowItem?.jobInformation?.jobPosition) ||
                      ""}
                  </Td>
                </Fragment>
              )}
              {location === "Payroll Table" && (
                <Fragment>
                  <Td>
                    <div className="employee-cell">
                      <img
                        src={
                          rowItem?.profilePicture ||
                          rowItem?.employeeProfilePicture ||
                          defaultHeadshot
                        }
                        alt={rowItem?.fullName || "Employee"}
                        onError={(e) => {
                          e.target.src = defaultHeadshot;
                        }}
                      />
                      <div className="employee-text">
                        <span className="employee-name">
                          {(() => {
                            const nameParts = (rowItem?.fullName || "").split(
                              " ",
                            );
                            const displayName =
                              nameParts.length >= 2
                                ? `${nameParts[1]} ${nameParts[0]}`
                                : rowItem?.fullName;
                            return capitalizeWords(displayName) || "";
                          })()}
                        </span>
                        <span className="employee-role">
                          {capitalizeWords(
                            rowItem?.jobInformation?.jobPosition,
                          ) || ""}
                        </span>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    {capitalizeWords(rowItem?.jobInformation?.department) || ""}
                  </Td>
                  <Td>
                    <BaseInput
                      type="checkbox"
                      checked={rowItem?.isExempted}
                      style={{
                        width: "auto",
                        flexShrink: 0,
                      }}
                      onChange={(e) =>
                        handleChange(e, rowItem?.employeeId, "exemption")
                      }
                    />
                  </Td>
                  <Td>
                    <BaseInput
                      type="number"
                      min={0}
                      max={31}
                      defaultValue={0}
                      style={{
                        padding: "2px",
                        width: "50px",
                        border: "1px solid transparent",
                        borderRadius: "1rem",
                        textAlign: "center",
                        backgroundColor: "#f0faf8",
                        color: "#059669",
                        fontWeight: 600,
                      }}
                      onChange={(e) =>
                        handleChange(e, rowItem?.employeeId, "prorate")
                      }
                    />
                  </Td>
                  {rowItem?.monthlySalary && (
                    <Td>{rowItem?.monthlySalary?.toLocaleString() || ""}</Td>
                  )}
                  {rowItem?.payrollVariables?.map((variable, index) => (
                    <Td key={index}>{variable?.value?.toLocaleString()}</Td>
                  ))}
                  <Td
                    onClick={(e) => handleRowItemClick(e, rowItem?.employeeId)}
                  >
                    Manage Employee
                  </Td>
                </Fragment>
              )}
              {location === "Summary Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.employeeFullName || "")}</Td>
                  <Td>{rowItem?.totalEarnings?.toLocaleString() || ""}</Td>
                  <Td>{rowItem?.totalDeductions?.toLocaleString() || ""}</Td>
                  <Td>{rowItem?.netPay?.toLocaleString() || ""}</Td>
                  <Td
                    className="view-payslip-link"
                    onClick={() =>
                      navigate(`/reportsummary/summary/${rowItem?.payslipId}`)
                    }
                  >
                    View Payslip
                  </Td>
                </Fragment>
              )}
              {location === "Employee Payslip Table" && (
                <Fragment>
                  <Td>{getMonthName(rowItem?.month, rowItem?.year) || ""}</Td>
                  <Td>{rowItem?.year || ""}</Td>
                  <Td
                    onClick={(e) => handleRowItemClick(e, rowItem?.payslipId)}
                  >
                    View Payslip
                  </Td>
                </Fragment>
              )}
              {location === "User Summary Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.name || "")}</Td>
                  <Td>{rowItem?.value?.toLocaleString() || ""}</Td>
                </Fragment>
              )}
              {location === "Variance Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.employeeFullName) || ""}</Td>
                  <Td>{rowItem?.firstMonthValue?.toLocaleString() || ""}</Td>
                  <Td>{rowItem?.secondMonthValue?.toLocaleString() || ""}</Td>
                  <Td>{rowItem?.variance || "0"}</Td>
                  <Td>{rowItem?.percentage || "0"}</Td>
                </Fragment>
              )}
              {location === "Departments Table" && (
                <Fragment>
                  <Td>{rowItem?.name ? capitalizeWords(rowItem?.name) : ""}</Td>
                  <Td
                    onClick={(e) =>
                      handleRowItemClick(e, rowItem?.departmentId)
                    }
                  >
                    <FontAwesomeIcon
                      icon={faEllipsisV}
                      style={{
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                    />
                    {activeRowId === rowItem?.departmentId && (
                      <ul ref={dropdownRef} className="drop-down">
                        <li
                          onClick={(e) =>
                            handleRowItemActionClick(
                              e,
                              rowItem?.departmentId,
                              "edit",
                            )
                          }
                        >
                          Edit Department
                        </li>
                        <li
                          onClick={(e) =>
                            handleRowItemActionClick(
                              e,
                              rowItem?.departmentId,
                              "delete",
                            )
                          }
                        >
                          Delete Department
                        </li>
                      </ul>
                    )}
                  </Td>
                </Fragment>
              )}
              {location === "Company Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.companyName || "")}</Td>
                  <Td>{rowItem?.employerEmail || ""}</Td>
                  <Td>{capitalizeWords(rowItem?.planType || "")}</Td>
                  <Td>{rowItem?.creditBalance || ""}</Td>
                  <Td>{rowItem?.lastUsedDate || ""}</Td>
                  <Td
                    onClick={(e) => handleRowItemClick(e, rowItem?.companyId)}
                  >
                    View Details
                  </Td>
                </Fragment>
              )}
              {location === "Referrals Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.refererFullname)}</Td>
                  <Td>{capitalizeWords(rowItem?.employerFullname)}</Td>
                  <Td>{capitalizeWords(rowItem?.companyName)}</Td>
                  <Td>
                    <span className="email-cell">{rowItem?.companyEmail}</span>
                  </Td>
                  <Td>{rowItem?.dateReferred}</Td>
                  <Td>
                    <span
                      className={`referral-status status-${rowItem?.status?.toLowerCase()?.replace(/\s+/g, "-") || "unknown"}`}
                    >
                      {capitalizeWords(rowItem?.status) || "Unknown"}
                    </span>
                  </Td>
                </Fragment>
              )}
              {location === "Plans & Pricing" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.title)}</Td>
                  <Td>{rowItem?.lowerLimit}</Td>
                  <Td>{rowItem?.upperLimit}</Td>
                  <Td>{rowItem?.creditCostPerEmployee}</Td>
                  <Td className="plans-pricing-actions">
                    <button
                      type="button"
                      title="Edit basic plan information (name, limits, and credit cost). For comprehensive plan editing including features, visit the plan details page."
                      onClick={(e) =>
                        handleRowItemActionClick?.(e, rowItem?.id, "edit")
                      }
                    >
                      Quick Edit
                    </button>
                  </Td>
                </Fragment>
              )}
              {location === "Billing Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.companyName)}</Td>
                  <Td>{rowItem?.employerEmail}</Td>
                  <Td>{rowItem?.creditAmount}</Td>
                  <Td>{rowItem?.creditCost?.toLocaleString()}</Td>
                  <Td>{rowItem?.dateInitiated}</Td>
                  <Td>{rowItem?.dateCompleted}</Td>
                  <Td>{capitalizeWords(rowItem?.payrollPlan)}</Td>
                </Fragment>
              )}
              {location === "General Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.employeeFullName)}</Td>
                  <Td>{capitalizeWords(rowItem?.department)}</Td>
                  <Td>{rowItem?.salaryBankName}</Td>
                  <Td>{rowItem?.salaryBankAccount}</Td>
                  <Td>{rowItem?.pensionFirmName || "-"}</Td>
                  <Td>{rowItem?.pensionAccount || "-"}</Td>
                  {rowItem?.earnings?.map((variable, index) => (
                    <Td key={index}>{variable?.value?.toLocaleString()}</Td>
                  ))}
                  {rowItem?.deductions?.map((variable, index) => (
                    <Td key={index}>{variable?.value?.toLocaleString()}</Td>
                  ))}
                  <Td>{rowItem?.totalEarnings?.toLocaleString()}</Td>
                  <Td>{rowItem?.totalDeductions?.toLocaleString()}</Td>
                  <Td>{rowItem?.grossPay?.toLocaleString() || "0"}</Td>
                  <Td>{rowItem?.netPay?.toLocaleString() || "0"}</Td>
                </Fragment>
              )}
              {location === "Paye Table" && (
                <Fragment>
                  <Td>
                    <div className="employee-cell">
                      <span className="avatar">
                        {(
                          capitalizeWords(rowItem?.employeeFullName) ||
                          "Unknown Employee"
                        )
                          .charAt(0)
                          .toUpperCase() || "E"}
                      </span>
                      <div className="employee-meta">
                        <span className="employee-name">
                          {capitalizeWords(rowItem?.employeeFullName) ||
                            "Unknown Employee"}
                        </span>
                        <span className="employee-caption">PAYE record</span>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <span className="tax-id">
                      {rowItem?.taxNumber || "Not available"}
                    </span>
                  </Td>
                  <Td>{rowItem?.grossPay?.toLocaleString() || 0}</Td>
                  <Td>
                    <span className="paye-badge">
                      {rowItem?.payeValue?.toLocaleString() || 0}
                    </span>
                  </Td>
                </Fragment>
              )}
              {location === "Pension Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.employeeFullName)}</Td>
                  <Td>{rowItem?.pensionFirmName || "-"}</Td>
                  <Td>{rowItem?.pensionAccount || "-"}</Td>
                  <Td>{rowItem?.grossPay?.toLocaleString() || 0}</Td>
                  <Td>
                    {rowItem?.employerPensionValue?.toLocaleString() || 0}
                  </Td>
                  <Td>
                    {rowItem?.employeePensionValue?.toLocaleString() || 0}
                  </Td>
                  <Td>{rowItem?.pensionValue?.toLocaleString()}</Td>
                </Fragment>
              )}
              {location === "Payroll Report Table" && (
                <Fragment>
                  <Td>{capitalizeWords(rowItem?.employeeFullName)}</Td>
                  <Td>{rowItem?.totalEarnings?.toLocaleString()}</Td>
                  <Td>{rowItem?.totalDeductions?.toLocaleString()}</Td>
                  <Td>{rowItem?.grossPay?.toLocaleString() || "0"}</Td>
                  <Td>{rowItem?.netPay?.toLocaleString() || "0"}</Td>
                  <Td
                    className="view-payslip-link"
                    onClick={() =>
                      navigate(`/reportsummary/summary/${rowItem?.payslipId}`)
                    }
                  >
                    View Payslip
                  </Td>
                </Fragment>
              )}
            </tr>
          );
        })}
      </tbody>
      {monetaryColumns.length > 0 && (
        <tfoot>
          <tr className="table-total-row">
            {columnTitles.map((_, columnIndex) => (
              <Td key={columnIndex}>
                {columnIndex === 0
                  ? "TOTAL"
                  : monetaryColumns.includes(columnIndex)
                    ? totals[columnIndex].toLocaleString()
                    : ""}
              </Td>
            ))}
          </tr>
        </tfoot>
      )}
    </TableWrapper>
  );
};
